import { BreakpointValue } from 'consts/breakpoints';
import styled from '@emotion/styled';
import { CleanButton } from 'ds/components/button';
import { rangeToGalleryHeight } from './config';

export const BORDER_PADDING_FIX = 1;

export const ShareWrapper = styled.div`
  margin-left: ${({ theme }) => theme.spacing(1)};
`;

export const WaterMarkDiv = styled.div<{ watermarkPath: string }>`
  position: absolute;
  left: 0;
  width: 100%;
  bottom: 0;
  height: 80px;
  background: url(${({ watermarkPath }) => watermarkPath}) left center / auto 80px no-repeat;
`;

export const ShareMobileWrapper = styled.div`
  ${({ theme }) => `
    position: absolute;
    top: ${theme.spacing(2)};
    left: ${theme.spacing(2)};
  `}
`;

export const BackButtonWrapper = styled.div`
  ${({ theme }) => `
    position: absolute;
    top: ${theme.spacing(2)};
    right: ${theme.spacing(2)};
  `}
`;

export const HeightAwareSliderItemContainer = styled.div<{ isFloorPlan?: boolean; }>`
  ${({ theme }) => Object.keys(rangeToGalleryHeight).map(key => (
    theme.media.showAt({ at: (+key as BreakpointValue) })`
      height: ${rangeToGalleryHeight[key]}px;
    `
  ))};
  position: relative;
  flex-grow: 1;
  & > div {
    border-radius: ${props => props.theme.borderRadius.tiny};
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    background-repeat: no-repeat;
    background-position: center;
    background-color: ${props => props.theme.colors.neutrals.white};
    transform-origin: top left;
    background-size: ${({ isFloorPlan }) => isFloorPlan ? 'contain' : 'cover'};
    line-height: 0;
    & > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

export const Root = styled.div``;

export const Slide = styled.div<{isFloorPlan: boolean, photos?: number}>`
  position: relative;
  grid-column-start: 5;
  grid-column-end: 6;
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  cursor: pointer;
  z-index: 1;

  img {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    ${({ isFloorPlan }) =>
      isFloorPlan ? 'object-fit: contain;' : 'object-fit: cover;'
    }
  }

  &:nth-of-type(2){
    grid-row-start: 2;
    grid-row-end: 3;
    border-width: 1px 1px 0 0;
  }

  &:nth-of-type(3){
    grid-row-start: 1;
    grid-row-end: 2;
    border-width: 0 1px 0 0;
  }
`;

export const SlideText = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.4);
  color: ${({ theme }) => theme.colors.neutrals.white};
  font-weight: 600;
  line-height: 1.33;
  text-align: center;
  text-shadow: ${({ theme }) => theme.shadow.level6};
`;

export const ImageContainer = styled.div<{ imageCount: number }>`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 1fr);


  ${({ theme }) => Object.keys(rangeToGalleryHeight).map(key => (
    theme.media.showAt({ at: (+key as BreakpointValue) })`
      height: ${rangeToGalleryHeight[key]}px;
    `
  ))}

`;

export const ControlsWrapper = styled.div<{ align: 'right' | 'left'}>`
  ${({ theme, align }) => `
    position: absolute;
    top: ${theme.spacing(2)};
    ${align}: ${theme.spacing(2)};
    display: flex;
    z-index: 2;
  `}
`;


export const CounterWrapper = styled.span<{short?: boolean}>`
  z-index: 2;
  position: absolute;
  bottom: 24px;
  right: 0;
  width: ${({ short }) => short ? '80%' : '100%'};
  text-align: center;
  & > button {
    pointer-events: none;
  }
`;

export const FloorplanButtonWrapper = styled.div`
  ${({ theme }) => `
    position: absolute;
    z-index: 2;
    bottom: ${theme.spacing(3)};
    left: ${theme.spacing(3)};
  `}
  ${({ theme }) => theme.media.showAt({ to: 2 }) `
    bottom: ${theme.spacing(2)};
    left: ${theme.spacing(2)};
  `}
`;

export const FloorplanButton = styled(CleanButton)`
  font-weight: 600;
`;

export const ThumbnailsWrapperInner = styled.div`
  position: relative;
  width: 100%;
  direction: ltr;
  & > div > div > div > div {
    border-radius: 0;
  }
`;

export const PagerWrapper = styled.div`
  direction: ltr;
  position: absolute;
  bottom: 22px;
  left: 50%;
  display: flex;
  padding: 2px 4px;
  transform: translateX(-50%);
  border-radius: ${({ theme }) => theme.borderRadius.smallestSideRound};
  background-color: rgba(0, 0, 0, 0.2);
`;

type Direction = 'next' | 'prev';

export interface ControlsArrowProps {
  dir: Direction;
  onClick(): void;
  disabled?: boolean;
}

export const ControlsBox = styled.div<{short?: boolean}>`
  position: absolute;
  top: 0;
  right: 0;
  width: ${({ short }) => short ? '80%' : '100%'};
  height: 100%;
  :hover svg {
    opacity: 1;
  }
`;

export const ControlsBoxMask = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

export const ControlsArrowWrapper = styled.span<ControlsArrowProps>`
  cursor: pointer;
  height: 44px;
  ${({ disabled }) => disabled ? 'pointer-events: none' : ''};
  svg {
    transition: opacity .2s ease;
    opacity: 0;
    transform: scaleX(${({ dir }) => dir === 'next' ? '-' : ''}1);
    path {
      fill: ${({ theme }) => theme.colors.neutrals.white};
    }
  }
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ dir }) => (dir === 'next' ? 'right:' : 'left:') + ('20px;')}
  ${({ theme, dir }) => theme.media.showAt({ at: 3 }) `
    ${dir === 'next' ? 'right:' : 'left:'}${'20px;'}
  `}
`;

export const MapViewButtonWrapper = styled.div`
  z-index: 2;
  position: absolute;
  bottom: 8px;
  padding: 16px;
  right: 8px;
  ${({ theme }) => theme.media.showAt({ to: 2 }) `
    right: 0;
    bottom: 0;
  `}
`;

export const FavoritesMobileButtonWrapper = styled.div`
  z-index: 2;
  position: absolute;
  right: 16px;
  top: 16px;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  display: flex;
  align-items: center;
  justify-content: center;

  background: ${({ theme }) => theme.colors.neutrals.white};
`;

export const MapViewWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colors.neutrals.grey7};
  z-index: 1;
`;

export const RelevancyLabelComponent = styled.div`
  position: absolute;
  ${({ theme }) => `
    top: ${theme.spacing(2)};
    right: ${theme.spacing(2)};
    color: ${theme.colors.neutrals.grey2};
  `}
  ${({ theme }) => theme.media.showAt({ from: 3 })`
    top: auto;
    bottom: ${theme.spacing(2)};
    color: ${theme.colors.neutrals.white};
  `}
`;

export const VideoTourButtonComponent = styled.div`
  position: absolute;
  ${({ theme }) => `
    top: ${theme.spacing(2)};
    left: ${theme.spacing(2)};
    color: ${theme.colors.neutrals.grey2};
  `}
  ${({ theme }) => theme.media.showAt({ from: 3 })`
    top: auto;
    bottom: ${theme.spacing(2)};
    color: ${theme.colors.neutrals.white};
  `}
`;

export const TargetMarkerWrapper = styled.div`
  top: 50%;
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  svg {
    margin-top: -36px;
    height: 72px;
    width: 72px;
  }
`;
